// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { Color, Font } from '../Commons/Graphics';
import { ErrorInformationStorage } from './ErrorInformationStorage';
import { TerceraLineStyleComboBoxUtils } from './UtilsClasses/TerceraTIFComboBoxUtils';
import { DynPropertyTypes } from './properties/DynPropertyTypes';
//
// Constructor
//
// #region
export class DynProperty {
    constructor(name, value, type, group, parentGroup) {
        //
        // Ractive control
        //
        this.AssignedControl = null;
        this.Intervals = undefined;
        this.DynPropertyRelationType = DynPropertyRelationType.Enability;
        // Private fields (прячем в специальном объекте)
        this.PRIVATE = {};
        this.name = name;
        if (Resources.IsResourcePresent(name)) {
            this.localizationKey = name;
        }
        else {
            const locKey = 'property.' + name;
            if (Resources.IsResourcePresent(locKey)) {
                this.localizationKey = locKey;
            }
            else if (Resources.IsResourcePresentEN(locKey)) {
                this.localizationKey = locKey;
            }
            else {
                this.localizationKey = name;
            }
        }
        this.value = value;
        this.type = !!type || type === 0 ? type : DynProperty.UNKNOWN;
        this.group = group || '';
        this.parentGroup = parentGroup || DynProperty.PARAM_GROUP;
        this.populatePublicFieldsOptional();
    }
    populatePublicFieldsOptional() {
        // Public fields optional
        this.enabled = true;
        this.visible = true;
        this.tooltip = undefined;
        this.sortIndex = 100;
        this.DynPropertyRelationType = DynPropertyRelationType.Enability;
        this.enabilityValue = null;
        this.assignedProperty = [];
        this.separatorGroup = '';
        this.increment = 1;
        this.decimalPlaces = 0;
        this.minimalValue = 0;
        this.maximalValue = 100;
        this.objectVariants = null;
        this.COMBOBOX_TYPE = -1;
        this.propertyComment = '';
        this.coefficient = -1;
        this.error = null;
        this.customCSS = null;
        this.BeforeChangeEvent = null;
    }
    // #endregion
    static fromJson(json) {
        try {
            const FType = parseInt(json.type);
            // Вытягиваем основные параметры
            const FName = json.name;
            const FGroup = json.group;
            const Value = json.value;
            let FValue = Value;
            const dpType = FType === DynProperty.COMBOBOX_COMBOITEM ? DynProperty.COMBOBOX : FType;
            const dp = new DynProperty(FName, Value, dpType, FGroup);
            if (json.hasOwnProperty('sortIndex')) {
                const sortIndex = parseInt(json.sortIndex);
                if (!isNaN(sortIndex))
                    dp.sortIndex = sortIndex;
            }
            // Присваиваем параметр Value и подгружаем дополнительные параметры
            switch (FType) {
                case DynProperty.COLOR:
                    FValue = DynProperty.colorInt32ToRGB(Value);
                    break;
                case DynProperty.CSSTYLE:
                case DynProperty.LINEWIDTH:
                case DynProperty.LINESTICK:
                case DynProperty.SYMBOL:
                case DynProperty.CHART_WINDOW:
                    FValue = parseInt(Value);
                    FValue = !isNaN(FValue) ? FValue : 0;
                    break;
                case DynProperty.STRING:
                    FValue = Value;
                    break;
                case DynProperty.INTEGER:
                    FValue = parseInt(Value);
                    FValue = !isNaN(FValue) ? FValue : 0;
                    if (json.min !== undefined) {
                        dp.minimalValue = parseFloat(json.min);
                    }
                    if (json.max !== undefined) {
                        dp.maximalValue = parseFloat(json.max);
                    }
                    break;
                case DynProperty.DOUBLE:
                case DynProperty.DOUBLE_WITH_REFRESH_BUTTON:
                    FValue = parseFloat(Value);
                    FValue = !isNaN(FValue) ? FValue : 0;
                    if (json.min !== undefined) {
                        dp.minimalValue = parseFloat(json.min);
                    }
                    if (json.max !== undefined) {
                        dp.maximalValue = parseFloat(json.max);
                    }
                    break;
                case DynProperty.BOOLEAN:
                case DynProperty.BOOLEAN_SOUND:
                    if (!(Value === null || Value === void 0 ? void 0 : Value.toLowerCase)) {
                        FValue = Value;
                    }
                    else {
                        FValue = Value.toLowerCase() === 'true';
                    }
                    break;
                case DynProperty.COLOR_STYLE_WIDTH:
                    if (Value != null) {
                        let c = Color.Empty;
                        let styleIndex = 0;
                        let widthIndex = 0;
                        let CheckBoxVisible = false;
                        let Checked = true;
                        const valuesStr = Value.split(';');
                        if (valuesStr.length > 0) {
                            CheckBoxVisible = valuesStr[0].toLowerCase() === 'true';
                            Checked = valuesStr[1].toLowerCase() === 'true';
                            c = DynProperty.colorInt32ToRGB(valuesStr[2]);
                            styleIndex = parseInt(valuesStr[3]);
                            widthIndex = parseInt(valuesStr[4]);
                        }
                        FValue = new ColorStyleWidth(c, styleIndex, widthIndex);
                        FValue.CheckBoxVisible = CheckBoxVisible;
                        FValue.Checked = Checked;
                    }
                    break;
                case DynProperty.COMBOBOX_COMBOITEM:
                    FValue = json.valueType == DynProperty.INTEGER ? parseInt(Value) : Value;
                    break;
                case DynProperty.COMBOBOX_COMBOITEM_TIF: {
                    const _ar = Value.split(';');
                    const _t = parseInt(_ar[0]);
                    const _d = parseInt(_ar[1]);
                    FValue = TerceraLineStyleComboBoxUtils.cretaComboItem(_t, new Date(_d));
                    break;
                }
                case DynProperty.FONT: {
                    const nameb = Value.indexOf('Name=') + 5;
                    const namee = Value.indexOf(',', nameb);
                    const fontName = Value.substring(nameb, namee);
                    const sizeb = Value.indexOf('Size=') + 5;
                    const sizee = Value.indexOf(',', sizeb);
                    const fontHeight = Value.substring(sizeb, sizee);
                    const fontWeight = json.bold === 'True' ? 'bold' : 'normal';
                    FValue = new Font(parseFloat(fontHeight), fontName, fontWeight);
                    break;
                }
                case DynProperty.ACCOUNT:
                    FValue = Value;
                    break;
                case DynProperty.INSTRUMENT:
                    FValue = Value;
                    break;
                case DynProperty.BOOL_NUMERIC:
                    FValue = new BoolNumeric(json.checked == '1', parseFloat(Value));
                    break;
                case DynProperty.PAIR_COLOR:
                    FValue = new PairColor(DynProperty.colorInt32ToRGB(Value.Color1), DynProperty.colorInt32ToRGB(Value.Color2), Value.ColorText1, Value.ColorText2);
                    FValue.DisableColor1 = Value.DisableColor1 === 'true';
                    FValue.DisableColor2 = Value.DisableColor2 === 'true';
                    break;
                case DynProperty.TRIO_COLOR:
                case DynProperty.TRIO_COLOR_GRADIENT:
                    FValue = new TrioColor(DynProperty.colorInt32ToRGB(Value.Color1), DynProperty.colorInt32ToRGB(Value.Color2), DynProperty.colorInt32ToRGB(Value.Color3), Value.ColorText1, Value.ColorText2, Value.ColorText3);
                    FValue.DisableColor1 = Value.DisableColor1 === 'true';
                    FValue.DisableColor2 = Value.DisableColor2 === 'true';
                    FValue.DisableColor3 = Value.DisableColor3 === 'true';
                    break;
                case DynProperty.RADIOBUTTON_COMBOITEM:
                case DynProperty.RADIOBUTTON_COMBOITEM_WITH_LABEL: {
                    const valueType = json.valueType;
                    if (valueType == 'boolean') {
                        FValue = Value === '1';
                    }
                    else if (valueType == 'number') {
                        FValue = parseFloat(Value);
                    }
                    else {
                        FValue = Value;
                    }
                    break;
                }
                case DynProperty.INSTRUMENTS_DEFAULTS:
                    FValue = Value;
                    break;
                default:
                    FValue = Value;
                    break;
            }
            if (json.decimalPlaces !== undefined) {
                dp.decimalPlaces = parseInt(json.decimalPlaces);
            }
            if (json.increment !== undefined) {
                dp.increment = parseFloat(json.increment);
            }
            dp.value = FValue;
            // For indicators only.
            const variants = json.variants;
            if (variants) {
                const keys = Object.keys(variants);
                const len = keys.length;
                const result = [];
                for (let i = 0; i < len; i++) {
                    const val = variants[keys[i]];
                    result.push({ value: val, text: val });
                }
                dp.objectVariants = result;
            }
            if (json.comment) {
                dp.propertyComment = json.comment;
            }
            return dp;
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
            console.log('DP err');
        }
    }
    // #region
    static isStyleProperty(type) {
        switch (type) {
            case DynProperty.COLOR:
            case DynProperty.COLOR_STYLE_WIDTH:
            case DynProperty.PAIR_COLOR:
            case DynProperty.TRIO_COLOR:
            case DynProperty.TRIO_COLOR_GRADIENT:
                return true;
            default:
                return false;
        }
    }
    GetLocalizedName() {
        return this.propertyComment ? this.propertyComment : Resources.getResource(this.localizationKey);
    }
    // Получение глубокой копии
    GetClone() {
        const clone = new DynProperty(this.name, this.value, this.type, this.group, this.parentGroup);
        clone.localizationKey = this.localizationKey;
        clone.objectVariants = this.objectVariants;
        clone.increment = this.increment;
        clone.decimalPlaces = this.decimalPlaces;
        clone.minimalValue = this.minimalValue;
        clone.maximalValue = this.maximalValue;
        clone.sortIndex = this.sortIndex;
        clone.tooltip = this.tooltip;
        clone.enabled = this.enabled;
        clone.visible = this.visible;
        clone.DynPropertyRelationType = this.DynPropertyRelationType;
        clone.enabilityValue = this.enabilityValue;
        clone.assignedProperty = this.assignedProperty;
        clone.separatorGroup = this.separatorGroup;
        clone.propertyComment = this.propertyComment;
        clone.coefficient = this.coefficient;
        clone.BeforeChangeEvent = this.BeforeChangeEvent;
        clone.callback = this.callback;
        return clone;
    }
    serialize() {
        const result = {};
        result.name = this.name;
        result.type = this.type;
        result.group = this.group;
        const value = this.value;
        switch (this.type) {
            case DynProperty.COLOR_STYLE_WIDTH:
                result.value =
                    value.CheckBoxVisible +
                        ';' +
                        value.Checked +
                        ';' +
                        DynProperty.colorRGBToInt32(value.Color) +
                        ';' +
                        value.Style +
                        ';' +
                        value.Width;
                break;
            case DynProperty.COLOR:
                result.value = DynProperty.colorRGBToInt32(value);
                break;
            case DynProperty.ACCOUNT:
                result.value = value;
                break;
            case DynProperty.INSTRUMENT:
                result.value = value;
                break;
            case DynProperty.BOOL_NUMERIC:
                result.value = value.Value;
                result.checked = value.Checked ? '1' : '0';
                break;
            case DynProperty.RADIOBUTTON_COMBOITEM:
            case DynProperty.RADIOBUTTON_COMBOITEM_WITH_LABEL: {
                const typeOfValue = typeof value;
                result.valueType = typeOfValue;
                if (typeOfValue == 'boolean') {
                    result.value = value ? '1' : '0';
                }
                else {
                    result.value = value;
                }
                break;
            }
            case DynProperty.COMBOBOX_COMBOITEM_TIF:
                if (!isNullOrUndefined(value.date)) {
                    result.value = value.tif.value + ';' + value.date.getTime();
                }
                else {
                    result.value = value.tif.value + ';0';
                }
                break;
            case DynProperty.FONT:
                // [Font: Name=Tahoma, Size=11.25, Units=2, GdiCharSet=1, GdiVerticalFont=False]
                result.value =
                    '[Font: Name=' +
                        value.Family +
                        ', Size=' +
                        value.Height +
                        ', Units=2, GdiCharSet=1, GdiVerticalFont=False]';
                result.bold = value.FontWeight === 'bold' ? 'True' : 'False';
                result.italic = 'False';
                break;
            case DynProperty.INSTRUMENTS_DEFAULTS:
                result.value = value;
                break;
            default:
                result.value = value;
                break;
        }
        return result;
    }
    // #endregion
    // Static method
    // #region
    // getPropValue (object or null)
    static getPropValue(properties, name) {
        const dProp = DynProperty.getPropertyByName(properties, name);
        return dProp != null ? dProp.value : null;
    }
    // getPropertyByName (DynPropety or null)
    static getPropertyByName(properties, name) {
        const prop = properties.find((prop) => !isNullOrUndefined(prop) && prop.name === name);
        if (isNullOrUndefined(prop)) {
            return null;
        }
        return prop;
    }
    static getPropertyByGroupAndName(properties, group, name) {
        return properties.find((prop) => !isNullOrUndefined(prop) && prop.group === group && prop.name === name);
    }
    static getPropertyIndex(dpArray, name) {
        if (!dpArray || !name) {
            return -1;
        }
        for (let i = 0, len = dpArray.length; i < len; i++) {
            const dp = dpArray[i];
            if (dp.name === name) {
                return i;
            }
        }
        return -1;
    }
    static isCorrect(dynProperty) {
        return dynProperty !== null && dynProperty.value !== null && dynProperty.value !== undefined;
    }
    // Обновить связанные проперти. Вызывается после изменения значения каждого DynProperty (внешне)
    // return - надо или нет перезаполнить свойства
    static UpdatePropertyRelation(props, mainProp) {
        const assignedProps = mainProp.assignedProperty;
        // в mainProp никто не заинтересован
        if (!assignedProps.length)
            return false;
        const mainPropEnabled = mainProp.enabled;
        let mainPropRelationVal;
        if (mainProp.type === DynProperty.COLOR_STYLE_WIDTH || mainProp.type === DynProperty.BOOL_NUMERIC) {
            mainPropRelationVal = !!(mainProp.value.Checked && mainPropEnabled);
        }
        else {
            mainPropRelationVal = mainProp.value;
        }
        const len = assignedProps.length;
        for (let i = 0; i < len; i++) {
            const prop = DynProperty.getPropertyByGroupAndName(props, mainProp.group, assignedProps[i]);
            if (!prop)
                continue;
            let newRelationVal = false;
            const propEnabilityVal = prop.enabilityValue;
            if (Array.isArray(propEnabilityVal)) {
                newRelationVal = propEnabilityVal.includes(mainPropRelationVal);
            }
            else {
                newRelationVal =
                    propEnabilityVal === null || propEnabilityVal === undefined
                        ? mainPropRelationVal
                        : mainPropRelationVal === propEnabilityVal;
            }
            const control = prop.AssignedControl;
            switch (mainProp.DynPropertyRelationType) {
                case DynPropertyRelationType.Enability:
                    prop.enabled = newRelationVal;
                    if (control) {
                        control.set('enabled', newRelationVal);
                    }
                    break;
                case DynPropertyRelationType.Visibility:
                    prop.visible = newRelationVal;
                    if (control) {
                        control.set('visible', newRelationVal);
                    }
                    break;
                case DynPropertyRelationType.Value:
                    if (control) {
                        control.Tag.value = newRelationVal;
                        control.set('value', newRelationVal);
                    }
                    break;
            }
            // рекурсивно проверяем, если есть AssignedProperty у текущей temp
            if (prop === null || prop === void 0 ? void 0 : prop.assignedProperty.length) {
                DynProperty.UpdatePropertyRelation(props, prop);
            }
        }
        return true;
    }
    // static Клонировать массив свойст
    static CloneDynProperties(array) {
        var _a;
        const result = [];
        for (let i = 0, max = array.length; i < max; i++) {
            if ((_a = array[i]) === null || _a === void 0 ? void 0 : _a.GetClone) {
                result.push(array[i].GetClone());
            }
        }
        return result;
    }
    static getLocalizedGroupStrings(group) {
        switch (group) {
            case DynProperty.PARAM_GROUP:
                return {
                    name: Resources.getResource('property.General'),
                    tooltip: Resources.getResource('property.General.ToolTip')
                };
            case DynProperty.VIEW_GROUP:
                return {
                    name: Resources.getResource('property.View'),
                    tooltip: Resources.getResource('property.View.ToolTip')
                };
            // case DynProperty.SOUNDS_GROUP:
            //     return {
            //         name: Resources.getResource("settings.sound"),
            //         tooltip: Resources.getResource("settings.sound.tt")
            //     };
            case DynProperty.TRADING_DEFAULTS_GROUP:
                return {
                    name: Resources.getResource('property.Defaults'),
                    tooltip: Resources.getResource('property.Defaults.ToolTip')
                };
            case DynProperty.CONFIRMATIONS_GROUP:
                return {
                    name: Resources.getResource('property.Confirmations'),
                    tooltip: Resources.getResource('property.Confirmations.ToolTip')
                };
            case DynProperty.WARNINGS_GROUP:
                return {
                    name: Resources.getResource('property.Warnings'),
                    tooltip: Resources.getResource('property.Warnings.ToolTip')
                };
            case DynProperty.HOTKEYS_GROUP:
                return {
                    name: Resources.getResource('property.Hotkeys'),
                    tooltip: Resources.getResource('property.Hotkeys.ToolTip')
                };
            // case DynProperty.BRANDING_GROUP:
            //     return {
            //         name: Resources.getResource("OnlineUsers.settings.branding"),
            //         tooltip: Resources.getResource("OnlineUsers.settings.branding")
            //     };
            default: {
                if (group.indexOf('#') > -1) {
                    // #97150
                    group = group.replace(' # ', ' ');
                    return {
                        name: group,
                        tooltip: group
                    };
                }
                const locKey = 'property.' + group;
                const locString = Resources.getResource(locKey);
                return {
                    name: locString,
                    tooltip: locString
                };
            }
        }
    }
    // #endregion
    static colorRGBToInt32(stringColor) {
        if (!stringColor) {
            stringColor = 'rgb(0,0,0)';
        }
        const data = stringColor
            .substring(stringColor.indexOf('('), stringColor.length)
            .replace(/[()]/g, '')
            .split(',');
        if (data.length === 3) {
            return (255 << 24) | (data[0] << 16) | (data[1] << 8) | data[2];
        }
        else if (data.length === 4) {
            const alpfa = parseFloat(data[3]) * 255;
            return (alpfa << 24) | (data[0] << 16) | (data[1] << 8) | data[2];
        }
        return stringColor;
    }
    static colorInt32ToRGB(intColor) {
        const data = parseInt(intColor);
        if (isNaN(data)) {
            return intColor;
        }
        const Blue = data & 255;
        const Green = (data >> 8) & 255;
        const Red = (data >> 16) & 255;
        const Alpha = (data >> 24) & 255;
        if (Alpha === 255) {
            return 'rgb(' + Red + ',' + Green + ',' + Blue + ')';
        }
        else {
            return 'rgba(' + Red + ',' + Green + ',' + Blue + ',' + Alpha / 255 + ')';
        }
    }
    static deserialize(serializedDynPropertyArray) {
        return serializedDynPropertyArray.map(function (obj) {
            return DynProperty.fromJson(obj);
        });
    }
    static serialize(dynPropertyArray) {
        return dynPropertyArray.map(function (prop) {
            return prop.serialize();
        });
    }
}
// Константы класса
// Переносить с КЛИЕНТА номера, новые начинаем с 1000
// #region
DynProperty.UNKNOWN = -1; // Неизвестные параметр
DynProperty.INTEGER = DynPropertyTypes.INTEGER; // тип - целое (int)
DynProperty.DOUBLE = DynPropertyTypes.DOUBLE; // тип - вещественное (double)
DynProperty.COLOR = 2; // тип - цвет (string)
DynProperty.STRING = 3; // тип - строка (string)
DynProperty.BOOLEAN = 4; // тип - булево (bool)
DynProperty.FONT = 26; // тип - шрифт (string)
DynProperty.COMBOBOX = DynPropertyTypes.COMBOBOX; // тип - набор объектов в комбобоксе?
DynProperty.DATETIME = 5; // тип - датавремя (double)
DynProperty.SYMBOL = 6; // тип - символ (картинка) (int)
DynProperty.DATE = 7; // тип - дата (double)
DynProperty.TIME = 8; // тип - время (double)
DynProperty.CSSTYLE = 11; // тип - вид линии (int)
DynProperty.LINEWIDTH = 12; // тип - толщина линии (int)
DynProperty.LINESTICK = 14; // тип - виды прилипания (high,low,open,close) (int)
DynProperty.PASSWORD = 50; // тип - пароль
DynProperty.LINK = 51; // тип - ссылка
DynProperty.LINK_WITH_TEXT = 52; // тип - текст и ссылка (кнопка refresh)
DynProperty.BOOLEAN_TEXT = 53; // тип - boolean c текстом
DynProperty.INSTRUMENT = 55; // тип - выбор инструмента
DynProperty.ACCOUNT = 57; // тип - выбор акаунта
DynProperty.FILENAME = 17; // тип - Имя файла
DynProperty.FOLDERNAME = 170; // тип - Имя директории
DynProperty.CELLBUTTON = 174; // кнопка в таблице QuickTable
DynProperty.CHART_WINDOW = 180; // тип - user-control для индексации окон на чарт-панели
DynProperty.FIBLEVEL = 182; // тип - для установки значенийи видимости уровней фибоначчи
DynProperty.PAIR_COLOR = 183; // тип - 2 колор кнопки
/**
 * @deprecated This type is no longer recommended sice 3.120. Use `COMBOBOX` instead.
 */
DynProperty.COMBOBOX_COMBOITEM = 185;
DynProperty.BOOL_NUMERIC = 186; // нумерик с чекбоксом
DynProperty.COLOR_STYLE_WIDTH = 187; // тип - установка цвета, стиля и размера линии
DynProperty.RADIOBUTTON_COMBOITEM = 188; // radiobutton работающий с Utils.ComboItem
DynProperty.RADIOBUTTON_COMBOITEM_WITH_LABEL = 189; // radiobuttons c label
DynProperty.RANGECONTROL = 210; // контрол определяет время начала/конца
DynProperty.SPARKLINE = 211; // тип - кривая графика
DynProperty.SYMBOL_WITH_IMGS = 212; // тип - имя инструмента + цветной кружок + иконка инструмента (портфолио)
DynProperty.TRIO_COLOR = 214; // тип - 3 колор кнопки
DynProperty.TRIO_COLOR_GRADIENT = 215;
DynProperty.DOUBLE_WITH_REFRESH_BUTTON = 216;
DynProperty.HOTKEYS = 217;
DynProperty.HOTKEY_SETTINGS_CONTAINER = 218;
/// <summary>
/// prop.ObjectVariants = new object[] {
/// new Utils.ComboItem(Resources.getResource("text1"), (int)enum.Value1),
/// new Utils.ComboItem(Resources.getResource("text2"),  (int)enum.Value2),};
/// </summary>
DynProperty.BOOLEAN_SOUND = 1000; //
DynProperty.COMBOBOX_COMBOITEM_TIF = DynPropertyTypes.COMBOBOX_COMBOITEM_TIF; //
DynProperty.INSTRUMENTS_DEFAULTS = 1002;
DynProperty.SLTP = 1003;
DynProperty.SEPARATOR = 1009;
DynProperty.GROUP_SEPARATOR = 1010;
DynProperty.ALERT_NOTIFICATION_SELECTOR = 1011;
DynProperty.BOOLEAN_EVT = 1012;
DynProperty.TRUNCATE_TEXT_ADDING_ELLIPSIS = 2000;
DynProperty.INFO_PICTURE = 2001;
DynProperty.INFO_PICTURE_RIGHT_AND_TEXT = 2002;
DynProperty.CLOSE_BUTTON = 2003;
DynProperty.EXPAND_SUPER_POSITION_BUTTON_AND_TEXT = 2004; // (FIFO) #80010
DynProperty.COMPONENT_OF_SUPER_POSITION_TEXT = 2005; // (FIFO) #80010
DynProperty.ORDER_BUTTON = 2006; // #86679
DynProperty.TRADE_BUTTON = 2007; // #87442
DynProperty.TRADING_CENTRAL_ITEM = 2008; // #86776
DynProperty.ITCHART_ADVANCED = 2009; // #87796
DynProperty.ALERT_BUTTONS_GROUP = 2010;
DynProperty.SELL_ASSET_BUTTON = 2011;
DynProperty.POSITION_SIZING = 2012; // #97813
DynProperty.PRODUCT_TYPE_AND_MQ = 2013; // #101641
DynProperty.ATTENTION_MSG = 2014; // #101641
DynProperty.DELAYED_PICTURE_RIGHT_AND_TEXT = 2015; // #112155
DynProperty.PLACE_BUTTON = 2016; // #117256
DynProperty.VISIBILITY_BUTTON = 2017; // #123434
DynProperty.SETTINGS_BUTTON = 2018; // #123434
DynProperty.SHOW_ON_CHART_BUTTON = 2019; // #123434
DynProperty.CLOSE_VIA_OE_BUTTON = 2020;
DynProperty.PARAM_GROUP = 'General';
DynProperty.VIEW_GROUP = 'View';
DynProperty.SOUNDS_GROUP = 'Sounds';
DynProperty.TRADING_DEFAULTS_GROUP = 'Trading Defaults';
DynProperty.CONFIRMATIONS_GROUP = 'Confirmations';
DynProperty.WARNINGS_GROUP = 'Warnings';
DynProperty.NOTIFICATIONS_GROUP = 'Notifications';
DynProperty.SCRIPT_PARAMETERS_GROUP = 'Parameters';
DynProperty.GENERAL_GROUP = 'General';
DynProperty.OPTION_CHAIN_GROUP = 'OptionChain';
DynProperty.ANALYZER_GROUP = 'Analyzer';
DynProperty.VOLATILITY_LAB_GROUP = 'Volatility Lab';
DynProperty.ANALYZER_INFO_WINDOW_GROUP = 'AnalyzerInfo';
DynProperty.HOTKEYS_GROUP = 'Hotkeys';
DynProperty.NUMERICAL_PARAMETRS_GROUP = 'Points';
DynProperty.VISUAL_TRADING_GROUP = 'Visual Trading';
DynProperty.TIME_SCALE_GROUP = 'Time Scale';
DynProperty.PRICE_SCALE_GROUP = 'Price Scale';
DynProperty.DATA_BOX_GROUP = 'Data Box';
DynProperty.DATA_STYLE_GROUP = 'Data Style';
DynProperty.CHART_OE_GROUP = 'Chart OE';
DynProperty.FXCELL_GROUP = 'FX Cell';
DynProperty.ALERTS_GROUP = 'Alerts';
DynProperty.VOLUME_GROUP = 'Volume';
DynProperty.BRANDING_GROUP = 'Branding';
DynProperty.VISUAL_GROUP = 'View';
// public static readonly SOUNDS_GROUP = "sounds_param";
DynProperty.COLORS_GROUP = 'colors_param';
DynProperty.HIDDEN_GROUP = 'hidden';
DynProperty.CONNECTION_GROUP = 'Mail Server';
DynProperty.INSTRUMENT_GROUP = 'Instrument Types';
DynProperty.SEPARATOR_GROUP1 = ' ';
DynProperty.SEPARATOR_GROUP2 = '  ';
DynProperty.SEPARATOR_GROUP3 = '   ';
DynProperty.COLUMNS = 'Columns';
DynProperty.PORTFOLIO_RETURN_GROUP = 'Portfolio return';
DynProperty.ASSET_RETURN_GROUP = 'Asset return';
DynProperty.PERCENT_SCALE_GROUP = 'Percent scale';
DynProperty.PORTFOLIO_BALANCE_SCALE = 'Portfolio balance scale';
DynProperty.FIBONACCI_PARAMETERS_GROUP = 'Fibonacci parameters';
export var DynPropertyRelationType;
(function (DynPropertyRelationType) {
    DynPropertyRelationType["Enability"] = "ENABILITY";
    DynPropertyRelationType["Visibility"] = "VISIBILITY";
    DynPropertyRelationType["Value"] = "VALUE";
})(DynPropertyRelationType || (DynPropertyRelationType = {}));
export class PairColor {
    constructor(color1, color2, colorText1, colorText2) {
        this.DisableColor1 = false;
        this.DisableColor2 = false;
        this.Color1 = color1;
        this.Color2 = color2;
        this.ColorText1 = colorText1 || '';
        this.ColorText2 = colorText2 || '';
    }
}
export class TrioColor {
    constructor(color1, color2, color3, colorText1, colorText2, colorText3) {
        this.DisableColor1 = false;
        this.DisableColor2 = false;
        this.DisableColor3 = false;
        this.Color1 = color1;
        this.Color2 = color2;
        this.Color3 = color3;
        this.ColorText1 = colorText1 || '';
        this.ColorText2 = colorText2 || '';
        this.ColorText3 = colorText3 || '';
    }
}
// -----------------
export class BoolNumeric {
    constructor(checked, value) {
        this.Checked = checked;
        this.Value = value;
    }
    toString() {
        return this.Checked.toString() + ';' + this.Value.toString();
    }
}
// -----------------
export class ColorStyleWidth {
    constructor(color, style, width) {
        this.Checked = true;
        this.DisableColor = false;
        this.DisableStyle = false;
        this.DisableWidth = false;
        this.CheckBoxVisible = false;
        this.Color = color;
        this.Style = style;
        this.Width = width || 1;
    }
    enableStyleOnly() {
        this.DisableColor = true;
        this.DisableWidth = true;
    }
}
